import { useEffect, useState, useCallback, useMemo } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  Utensils,
  Package,
  IndianRupee,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_consumption_report,
  get_report_filter_data,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import { handleError } from "../CommonJquery/CommonJquery";

export default function ConsumptionReport() {
  const { toast } = useToast();

  const [filterOption, setFilterOption] = useState("");
  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [menuItemId, setMenuItemId] = useState("all");
  const [ingredientId, setIngredientId] = useState("all");
  const [departmentId, setDepartmentId] = useState("all");

  const [departments, setDepartments] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [consumptionData, setConsumptionData] = useState([]);
  const [menuItemBreakdown, setMenuItemBreakdown] = useState([]);
  const [ingredientBreakdown, setIngredientBreakdown] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  const [stats, setStats] = useState({
    totalEntries: 0,
    totalQty: 0,
    totalValue: 0,
  });

  // Backend sends department_id only (no department_name) in consumption_data.
  // Build a lookup map from the filter-data departments list to resolve names client-side.
  const departmentMap = useMemo(() => {
    const map = {};
    departments.forEach((d) => {
      map[String(d.primary_id)] = d.department_name;
    });
    return map;
  }, [departments]);

  const formatNumber = (num) => {
    if (num === "-") return "-";
    if (num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(2));
  };

  const formatDateLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString.replace(" ", "T")).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fetchFilterData = useCallback(async () => {
    try {
      const fd = new FormData();
      const response = await server_post_data(get_report_filter_data, fd);
      if (!response.data.error) {
        setDepartments(response.data.message.departments || []);
        setMenuItems(response.data.message.menu_items || []);
        setIngredients(response.data.message.ingredients || []);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  }, []);

  const fetchConsumptionData = useCallback(
    async (start_date, end_date, flag = "1") => {
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startCal || !endCal) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
          start_date = startCal;
          end_date = endCal;
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", formatDateLocal(start_date));
      fd.append("end_date", formatDateLocal(end_date));
      if (menuItemId !== "all") fd.append("menu_item_id", menuItemId);
      if (ingredientId !== "all") fd.append("ingredient_id", ingredientId);
      if (departmentId !== "all") fd.append("department_id", departmentId);

      try {
        const response = await server_post_data(get_consumption_report, fd);
        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          // Backend returns { report_name, date_range, summary, filters_applied, data } directly at top level
          const payload = response.data || {};
          const summary = payload.summary || {};

          const SOURCE_NAME = {
            STOCK_ADJUSTMENT: "Adjustment",
            AUDIT_ADJUSTMENT: "Audit (short)",
          };

          // Sale ke alawa rows me menu item nahi hota — source dikhao
          const data = (payload.data || []).map((r) => ({
            ...r,
            menu_item_name:
              r.menu_item_name || SOURCE_NAME[r.source] || r.source || "-",
          }));

          // Menu item ka "total qty" pehle alag ingredients ke gram + ml jod
          // ke banta tha — ab orders count
          const menuWise = (
            summary.menu_item_wise ||
            summary.menu_item_breakdown ||
            []
          ).map((m) => ({
            menu_item_name: m.menu_item_name,
            total_qty: m.orders ?? m.total_qty ?? 0,
            total_value: m.value ?? m.total_value ?? 0,
            entries: m.orders ?? m.entries ?? 0,
          }));

          // Ingredient wise qty sahi hai (ek hi unit) — unit saath rakho
          const ingWise = (
            summary.ingredient_wise ||
            summary.ingredient_breakdown ||
            []
          ).map((i) => ({
            ingredient_name: i.ingredient_name,
            total_qty: i.quantity ?? i.total_qty ?? 0,
            unit: i.unit || "",
            total_value: i.value ?? i.total_value ?? 0,
            entries: i.count ?? i.entries ?? 0,
          }));

          setConsumptionData(data);
          setMenuItemBreakdown(menuWise);
          setIngredientBreakdown(ingWise);
          setCurrentPage(1);
          setStats({
            totalEntries:
              summary.total_entries ?? summary.total_consumption_entries ?? 0,
            totalQty: ingWise.length,
            totalValue:
              summary.total_value ?? summary.total_consumption_value ?? 0,
          });
        }
      } catch (error) {
        console.error("Error fetching consumption data:", error);
        toast({
          title: "Error",
          description: "Failed to load consumption data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [
      filterOption,
      startCal,
      endCal,
      menuItemId,
      ingredientId,
      departmentId,
      toast,
    ],
  );

  const handleSearch = useCallback(() => {
    fetchConsumptionData(startCal, endCal, "2");
  }, [startCal, endCal, fetchConsumptionData]);

  function downloadExcel(rows, filename = "Consumption_Report.xlsx") {
    if (!rows.length) {
      handleError("No data available for export!");
      return;
    }

    const detailSheetData = rows.map((item, index) => ({
      "Sr. No.": index + 1,
      Date: formatDate(item.date),
      "Menu Item": item.menu_item_name || "-",
      Ingredient: item.ingredient_name || "-",
      Department: departmentMap[String(item.department_id)] || "-",
      Quantity: formatNumber(item.quantity),
      Unit: item.unit || "-",
      "Rate (₹)": formatNumber(item.rate),
      "Value (₹)": formatNumber(item.value),
      "Reference Bill": item.order_uuid || "-",
    }));

    detailSheetData.push({
      "Sr. No.": "",
      Date: "",
      "Menu Item": "",
      Ingredient: "",
      Department: "TOTAL",
      Quantity: "",
      Unit: "",
      "Rate (₹)": "",
      "Value (₹)": formatNumber(stats.totalValue),
      "Reference Bill": `Entries: ${stats.totalEntries}`,
    });

    const menuSheetData = menuItemBreakdown.map((item) => ({
      "Menu Item": item.menu_item_name || "-",
      Orders: item.total_qty || 0,
      "Total Value (₹)": formatNumber(item.total_value),
      Entries: item.entries || 0,
    }));

    const ingredientSheetData = ingredientBreakdown.map((item) => ({
      Ingredient: item.ingredient_name || "-",
      "Total Qty": `${formatNumber(item.total_qty)} ${item.unit || ""}`.trim(),
      "Total Value (₹)": formatNumber(item.total_value),
      Entries: item.entries || 0,
    }));

    const detailSheet = XLSX.utils.json_to_sheet(detailSheetData);
    detailSheet["!cols"] = [
      { wch: 8 },
      { wch: 12 },
      { wch: 22 },
      { wch: 22 },
      { wch: 18 },
      { wch: 10 },
      { wch: 8 },
      { wch: 10 },
      { wch: 12 },
      { wch: 16 },
    ];

    const menuSheet = XLSX.utils.json_to_sheet(menuSheetData);
    menuSheet["!cols"] = [{ wch: 25 }, { wch: 12 }, { wch: 16 }, { wch: 10 }];

    const ingredientSheet = XLSX.utils.json_to_sheet(ingredientSheetData);
    ingredientSheet["!cols"] = [
      { wch: 25 },
      { wch: 12 },
      { wch: 16 },
      { wch: 10 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, detailSheet, "Consumption Detail");
    XLSX.utils.book_append_sheet(workbook, menuSheet, "By Menu Item");
    XLSX.utils.book_append_sheet(workbook, ingredientSheet, "By Ingredient");
    XLSX.writeFile(workbook, filename);
  }

  const totalPages = Math.ceil(consumptionData.length / entriesPerPage);
  const currentData = consumptionData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    fetchFilterData();
    fetchConsumptionData(startCal, endCal, "1");
  }, []);

  return (
    <MainLayout>
      <Header
        title="Consumption Report"
        subtitle="Ingredient consumption from orders and sales"
      />

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalEntries}
                </p>
                <p className="text-sm text-muted-foreground">
                  Consumption Entries
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Utensils className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalQty}
                </p>
                <p className="text-sm text-muted-foreground">
                  Ingredients Used
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-destructive">
                  ₹{formatNumber(stats.totalValue)}
                </p>
                <p className="text-sm text-muted-foreground">Total Value</p>
              </div>
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              <div className="space-y-2">
                <Label>Menu Item</Label>
                <Select value={menuItemId} onValueChange={setMenuItemId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Menu Items" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Menu Items</SelectItem>
                    {menuItems.map((mi) => (
                      <SelectItem
                        key={mi.primary_id}
                        value={String(mi.primary_id)}
                      >
                        {mi.menu_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Ingredient</Label>
                <Select value={ingredientId} onValueChange={setIngredientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Ingredients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ingredients</SelectItem>
                    {ingredients.map((ing) => (
                      <SelectItem
                        key={ing.primary_id}
                        value={String(ing.primary_id)}
                      >
                        {ing.ingredient_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={departmentId} onValueChange={setDepartmentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem
                        key={dept.primary_id}
                        value={String(dept.primary_id)}
                      >
                        {dept.department_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" /> Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(consumptionData)}
                  className="gap-2"
                  disabled={consumptionData.length === 0}
                >
                  <FileDown className="w-4 h-4" /> Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">
                    Menu Item
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Ingredient
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Qty
                  </TableHead>
                  <TableHead className="text-muted-foreground">Unit</TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Rate (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Value (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Ref. Bill
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => (
                    <TableRow
                      key={row.log_id || index}
                      className="border-border"
                    >
                      <TableCell>
                        {(currentPage - 1) * entriesPerPage + index + 1}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(row.date)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {row.menu_item_name || "-"}
                      </TableCell>
                      <TableCell>{row.ingredient_name || "-"}</TableCell>
                      <TableCell>
                        {departmentMap[String(row.department_id)] || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.quantity)}
                      </TableCell>
                      <TableCell>{row.unit || "-"}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.rate)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-destructive">
                        {formatNumber(row.value)}
                      </TableCell>
                      <TableCell>{row.order_uuid || "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, consumptionData.length)}{" "}
                of {consumptionData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
